/**
 * StatusPill (shared, PR-37/PR-38 subscriber-facing subset).
 *
 * Compact label for a task's lifecycle state. The operator dashboard uses a
 * richer set (agent_draft, eric_reviewing, ready_to_send) per PR-38; the
 * subscriber-facing portal collapses the operator-internal states into
 * subscriber-meaningful language so we never leak operator workflow vocabulary
 * into the client surface. The text content is always present (not color
 * only) per the accessibility commitment in design-guidelines §6.
 *
 * Final design pass (Bold + Rose): the pill adopts the prototype `Tag` visual
 * language (2px radius, uppercase, 0.05em tracking, Work Sans 500). Each status
 * maps to one of the Tag color variants: linen (idle/queued), rose (Eric is on
 * it / awaiting review), green (approved). Behavior and labels are unchanged.
 */

import type { TaskStatus } from '@studio/api-schemas';

type TagVariant = 'linen' | 'rose' | 'green';

interface PillStyle {
  label: string;
  variant: TagVariant;
}

const TAG_VARIANT_CLASS: Record<TagVariant, string> = {
  linen:
    'bg-[color:var(--color-linen)] text-[color:var(--color-navy)] border border-[color:var(--color-line-dark)]',
  rose: 'bg-[color:var(--color-rose)] text-[color:var(--color-navy)]',
  green:
    'bg-[color:var(--color-green-accent-subtle)] text-[color:var(--color-green-accent)] border border-[color:var(--color-green-accent)]',
};

/**
 * Subscriber-facing label per status. Operator-internal states map to the
 * subscriber's mental model: anything Eric is working on reads as "In
 * progress"; the publish-gate states are not exposed.
 */
const SUBSCRIBER_PILL: Record<TaskStatus, PillStyle> = {
  intake_shape_check: { label: 'Shaping', variant: 'linen' },
  backlog: { label: 'In backlog', variant: 'linen' },
  ready_to_pull: { label: 'Up next', variant: 'linen' },
  pulled: { label: 'Eric reviewing', variant: 'rose' },
  in_progress: { label: 'In progress', variant: 'rose' },
  agent_draft: { label: 'In progress', variant: 'rose' },
  eric_reviewing: { label: 'In progress', variant: 'rose' },
  ready_to_deliver: { label: 'In progress', variant: 'rose' },
  delivered: { label: 'Awaiting your review', variant: 'rose' },
  approved: { label: 'Approved', variant: 'green' },
  split: { label: 'Split into smaller tasks', variant: 'linen' },
};

export function StatusPill({ status }: { status: TaskStatus }) {
  const pill = SUBSCRIBER_PILL[status];
  return (
    <span
      className={[
        'inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em]',
        TAG_VARIANT_CLASS[pill.variant],
      ].join(' ')}
    >
      {pill.label}
    </span>
  );
}
